import {
  faEnvelope,
  faMagnifyingGlass,
  faMegaphone,
  faMoneyBillWave,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Group } from '@mantine/core';
import type { TFunction } from 'i18next';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import type { ColumnRenderProps } from 'react-table';

import type { IColumn } from '@feathr/components';
import { ContextMenu, Pill, TableColumnHeader, Text, Time } from '@feathr/components';
import { getIconForAction, TimeFormat } from '@feathr/hooks';

import DemoMenuItem from '../components/DemoMenuItem';
import type { ISampleCampaign, TSampleCampaignChannel } from '../sampleData';
import { statusLabel, statusTheme } from '../utils/statusPalette';
import buildOptionsColumn from './buildOptionsColumn';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

const CHANNEL_ICON: Record<TSampleCampaignChannel, typeof faEnvelope> = {
  email: faEnvelope,
  ads: faMegaphone,
  search: faMagnifyingGlass,
  monetization: faMoneyBillWave,
};

interface ICampaignColumnsArgs {
  base: string;
  t: TFunction;
}

function campaignColumns({ base, t }: ICampaignColumnsArgs): Array<IColumn<ISampleCampaign>> {
  return [
    {
      id: 'name',
      Header: TableColumnHeader({ title: t('Name'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      minWidth: 300,
      Cell({ original }): JSX.Element {
        return (
          <>
            <Link to={base}>{original.name}</Link>
            <Group gap={6}>
              <Pill theme={'orange'}>{original.type}</Pill>
              <Pill theme={statusTheme(original.status)}>{statusLabel(original.status)}</Pill>
              <FontAwesomeIcon icon={CHANNEL_ICON[original.channel]} />
            </Group>
            <Text variant={'smallRegular'}>{original.objective}</Text>
          </>
        );
      },
      Footer(): JSX.Element {
        return <strong>{t('Page totals')}</strong>;
      },
    },
    {
      id: 'project',
      Header: TableColumnHeader({ title: t('Project') }),
      sortable: false,
      width: 200,
      className: tableStyles.cell,
      Cell({ original }): JSX.Element {
        if (!original.project) {
          return <>{t('—')}</>;
        }
        return <Link to={base}>{original.project}</Link>;
      },
    },
    {
      accessor: 'dateCreated',
      id: 'date_created',
      Header: TableColumnHeader({ title: t('Created'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 110,
      Cell({ original }): JSX.Element {
        return <Time format={TimeFormat.shortDate} timestamp={original.dateCreated} />;
      },
    },
    {
      accessor: (row): string => row.dateStart ?? '',
      id: 'date_start',
      Header: TableColumnHeader({ title: t('Start'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 110,
      Cell({ original }): JSX.Element {
        return original.dateStart ? (
          <Time format={TimeFormat.shortDate} timestamp={original.dateStart} />
        ) : (
          <>-</>
        );
      },
    },
    {
      accessor: (row): string => row.dateEnd ?? '',
      id: 'date_end',
      Header: TableColumnHeader({ title: t('End'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 110,
      Cell({ original }): JSX.Element {
        return original.dateEnd ? (
          <Time format={TimeFormat.shortDate} timestamp={original.dateEnd} />
        ) : (
          <>-</>
        );
      },
    },
    {
      accessor: 'views',
      id: 'views',
      Header: TableColumnHeader({ title: t('Views'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 110,
      Cell({ original }): JSX.Element {
        return <>{numeral(original.views).format('0,0')}</>;
      },
      Footer({ data }: ColumnRenderProps<{ _original: ISampleCampaign }>): JSX.Element {
        const total = data.reduce((subtotal, row) => subtotal + row._original.views, 0);
        return <>{numeral(total).format('0,0')}</>;
      },
    },
    {
      accessor: 'clicks',
      id: 'clicks',
      Header: TableColumnHeader({ title: t('Clicks'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 100,
      Cell({ original }): JSX.Element {
        return <>{numeral(original.clicks).format('0,0')}</>;
      },
      Footer({ data }: ColumnRenderProps<{ _original: ISampleCampaign }>): JSX.Element {
        const total = data.reduce((subtotal, row) => subtotal + row._original.clicks, 0);
        return <>{numeral(total).format('0,0')}</>;
      },
    },
    {
      accessor: 'conversions',
      id: 'conversions',
      Header: TableColumnHeader({ title: t('Conversions'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 130,
      Cell({ original }): JSX.Element {
        return <>{numeral(original.conversions).format('0,0')}</>;
      },
      Footer({ data }: ColumnRenderProps<{ _original: ISampleCampaign }>): JSX.Element {
        const total = data.reduce((subtotal, row) => subtotal + row._original.conversions, 0);
        return <>{numeral(total).format('0,0')}</>;
      },
    },
    buildOptionsColumn<ISampleCampaign>({
      t,
      renderItems: (): JSX.Element => (
        <>
          <ContextMenu.Item link={base} prefix={getIconForAction('edit')}>
            {t('Edit')}
          </ContextMenu.Item>
          <DemoMenuItem action={'duplicate'} label={t('Duplicate')} />
          <DemoMenuItem action={'move'} label={t('Move')} />
          <DemoMenuItem action={'delete'} isDestructive={true} label={t('Delete')} />
        </>
      ),
    }),
  ];
}

export default campaignColumns;
